/**
 * Shop-by-category section enhancement.
 *
 * Custom element <shop-by-category> consumes the theme's central "Category
 * system" config (settings.category_*), surfaced via `data-shop-by-category-*`
 * attributes set by sections/shop-by-category.liquid. Two data sources:
 *
 *   • source = "json"      Fetch a nested JSON tree (URL from
 *                          `data-shop-by-category-url`), render cards client-side.
 *   • source = "metafield" Cards are already server-rendered by Liquid; JS only
 *                          hydrates: builds the search index from the DOM and
 *                          binds the filter, expand/collapse toggles, overflow.
 *
 * Both paths share the data layer in assets/category-data.js
 * (`theme.categories`): fetch + sessionStorage cache + diacritic-insensitive
 * search helpers. category-data.js is loaded immediately before this file.
 *
 * Common to both: search filter with <mark> highlight, per-card expand toggle
 * when content overflows, aborts inflight fetch + tears down listeners on
 * disconnect.
 *
 * EMPTY CATEGORIES (`settings.category_show_empty` → `data-shop-by-category-
 * show-empty`, ADR-0015). Only the JSON source filters here — metafield cards
 * arrive pre-filtered from Liquid, which has the real product counts. The JSON
 * tree is filterable only when whoever generates the file emits a
 * `products_count` per node; `theme.categories.pruneEmpty` then drops every
 * subtree known to hold nothing, before render and before the JSON-LD. Nodes
 * without a count are always kept. Unlike the category rail, this grid never
 * probes products.json — one request per node across a whole tree is not a
 * trade worth making.
 */

(function () {
  'use strict';

  const theme = window.theme || {};

  // Strip HTML so user-controlled JSON content can never inject markup. Required,
  // not optional: this element builds card markup as strings, and an escaper that
  // silently degraded to identity would turn a merchant-supplied category name
  // into an injection vector. utils.js is a <head> foundation script, so its
  // absence means the page is broken anyway.
  if (!theme.utils || !theme.utils.escapeHtml) return;
  if (customElements.get('shop-by-category')) return;

  const cats = theme.categories || {};
  const escapeHtml = theme.utils.escapeHtml;

  // Diacritic-insensitive search helpers come from the shared data layer.
  const normalize = cats.normalize || ((s) => String(s).toLowerCase());
  const buildNormMap = cats.buildNormMap;

  // Mirrors snippets/icon-plus.liquid (same paths/viewBox/stroke/class hooks).
  // The `.v-line` hook lets CSS render the same SVG as a minus when expanded
  // (`.shop-by-category__toggle[aria-expanded='true'] .v-line { display: none }`).
  const PLUS_SVG =
    '<svg width="24" height="24" viewBox="0 0 256 256" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon icon--plus"><line class="h-line" x1="40" y1="128" x2="216" y2="128"/><line class="v-line" x1="128" y1="40" x2="128" y2="216"/></svg>';

  class ShopByCategory extends HTMLElement {
    connectedCallback() {
      this.source = this.dataset.shopByCategorySource || 'json';
      this.dataUrl = this.dataset.shopByCategoryUrl || '';
      this.expandLabel = this.dataset.shopByCategoryExpandLabel || 'Expand';
      this.collapseLabel = this.dataset.shopByCategoryCollapseLabel || 'Collapse';
      // Drop dead-end cards? Read the way compare reads its twin setting
      // (`data-compare-show-empty` in compare-modal.js): only an explicit "false"
      // filters, so a missing attribute can never remove a category. Scoped to the
      // JSON source — metafield cards arrive already filtered from Liquid, which
      // is the side that has the real product counts.
      this.dropEmpty = this.dataset.shopByCategoryShowEmpty === 'false' && this.source === 'json';

      this.grid = this.querySelector('[data-shop-by-category-grid]');
      this.input = this.querySelector('[data-shop-by-category-search]');
      this.searchWrap = this.querySelector('.shop-by-category__search');
      this.editorEmpty = this.querySelector('[data-shop-by-category-editor-empty]');
      this.empty = this.querySelector('[data-shop-by-category-empty]');
      this.errorEl = this.querySelector('[data-shop-by-category-error]');
      this.retryBtn = this.querySelector('[data-shop-by-category-retry]');
      this.status = this.querySelector('[data-shop-by-category-status]');

      this.controller = null;
      // Persistent cleanups live for the element's lifetime (retry button).
      // Fetch cleanups are torn down on every retry/refetch so listeners,
      // observers, and injected <script type="application/ld+json"> blocks
      // don't accumulate.
      this.persistentCleanups = [];
      this.fetchCleanups = [];
      this.rafId = null;
      this.itemIndex = [];
      this.cards = [];

      this.onInput = this.onInput.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onRetry = this.onRetry.bind(this);

      if (this.retryBtn) {
        this.retryBtn.addEventListener('click', this.onRetry);
        this.persistentCleanups.push(() => this.retryBtn.removeEventListener('click', this.onRetry));
      }

      if (this.source === 'metafield') {
        this.hydrateFromDom();
      } else {
        this.fetchAndRender();
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.runFetchCleanups();
      this.persistentCleanups.forEach((fn) => fn());
      this.persistentCleanups = [];
    }

    runFetchCleanups() {
      this.fetchCleanups.forEach((fn) => fn());
      this.fetchCleanups = [];
    }

    /* ---------- Metafield source: hydrate server-rendered cards ---------- */

    /**
     * Cards + nested lists are already in the DOM (rendered by Liquid from the
     * children metafield). Build the search index from the DOM, then enable the
     * clamp (`.is-hydrated`) and wire up the same filter / toggle / overflow
     * behaviour the JSON path uses.
     */
    hydrateFromDom() {
      this.cards = Array.from(this.querySelectorAll('[data-shop-by-category-card]'));
      this.bindToggles();
      this.buildIndexFromDom();
      this.bindFilter();
      this.bindResize();
      // Enable the max-height clamp now that JS can offer an expand toggle.
      this.classList.add('is-hydrated');
      this.measureOverflowSync();
      if (this.grid) this.grid.setAttribute('aria-busy', 'false');
    }

    buildIndexFromDom() {
      this.itemIndex = [];
      this.cards.forEach((card) => {
        const heading = card.querySelector('[data-shop-by-category-name]');
        const headingLink = heading ? heading.querySelector('a') || heading : null;
        const name = (headingLink ? headingLink.textContent : '').trim();

        this.itemIndex.push({ type: 'card', original: name, normalized: normalize(name), link: headingLink, lineage: [], card });

        const rootUl = card.querySelector('.shop-by-category__list');
        if (rootUl) this.indexListFromDom(rootUl, card, []);
      });
    }

    indexListFromDom(ulEl, card, ancestorLis) {
      const liEls = Array.from(ulEl.children).filter((el) => el.tagName === 'LI');
      liEls.forEach((li) => {
        const link = li.querySelector(':scope > a');
        const name = (link ? link.textContent : '').trim();
        const lineage = ancestorLis.concat(li);

        this.itemIndex.push({ type: 'item', original: name, normalized: normalize(name), link, lineage, card });

        const childUl = li.querySelector(':scope > ul');
        if (childUl) this.indexListFromDom(childUl, card, lineage);
      });
    }

    /* ---------- JSON source: fetch + render ------------------------------ */

    onRetry() {
      if (this.errorEl) this.errorEl.hidden = true;
      this.grid.hidden = false;
      this.grid.setAttribute('aria-busy', 'true');
      this.fetchAndRender();
    }

    /**
     * Hydrate from cached JSON when available, fall back to network fetch.
     * Layered to eliminate the visible "loading" state without a skeleton:
     *   1. sessionStorage cache → instant synchronous render on repeat visits.
     *   2. `<link rel="preload" as="fetch">` primes the HTTP cache so the
     *      first-visit fetch() is typically a cache hit.
     *   3. `measureOverflowSync` paints the fade gradient on the first frame.
     */
    async fetchAndRender() {
      if (!this.dataUrl || !cats.load) {
        this.showError();
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.runFetchCleanups();

      // Synchronous render path from sessionStorage — no waiting on fetch().
      const cached = cats.readCache ? cats.readCache(this.dataUrl) : null;
      if (cached) {
        this.applyData(cached);
        return;
      }

      try {
        const data = await cats.load(this.dataUrl, this.controller.signal);
        this.applyData(data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[shop-by-category] fetch failed:', err);
        this.showError();
      }
    }

    applyData(data) {
      // Prune BEFORE render and before the JSON-LD, so a dead-end category is
      // neither shown to a visitor nor advertised to a crawler.
      const tree = this.dropEmpty && cats.pruneEmpty ? cats.pruneEmpty(data) : data;
      this.renderCards(tree);
      this.injectJsonLd(tree);
      this.bindFilter();
      this.bindResize();
      this.grid.setAttribute('aria-busy', 'false');
    }

    showError() {
      this.grid.hidden = true;
      if (this.errorEl) this.errorEl.hidden = false;
      this.grid.setAttribute('aria-busy', 'false');
    }

    /**
     * No category survived (every one filtered out, or an empty tree). Collapse
     * to the heading rather than leaving an orphan search box over an empty
     * grid — and say why in the theme editor, where the note is rendered.
     * Deliberately silent for live visitors: "no results" belongs to the search
     * filter, not to a catalogue that simply has nothing to show.
     */
    showNoCategories() {
      this.grid.innerHTML = '';
      this.grid.hidden = true;
      if (this.searchWrap) this.searchWrap.hidden = true;
      if (this.editorEmpty) this.editorEmpty.hidden = false;
    }

    /** Render top-level categories as cards. Replaces the empty grid. */
    renderCards(data) {
      if (!data || typeof data !== 'object') {
        this.showNoCategories();
        return;
      }

      const entries = Object.entries(data);
      if (entries.length === 0) {
        this.showNoCategories();
        return;
      }

      const cards = entries.map(([name, categoryData], index) => {
        const subList = categoryData && categoryData.subcategories ? this.buildList(categoryData.subcategories) : '';
        const categoryHandle = categoryData && categoryData.handle ? categoryData.handle : '';
        const headingHtml = categoryHandle ? '<a href="/collections/' + escapeHtml(categoryHandle) + '">' + escapeHtml(name) + '</a>' : escapeHtml(name);

        return (
          '<div class="shop-by-category__card" data-shop-by-category-card>' +
          '<h2 class="shop-by-category__card-title" data-shop-by-category-name>' +
          headingHtml +
          '</h2>' +
          '<div class="shop-by-category__card-content" id="shop-by-category-content-' +
          index +
          '" data-shop-by-category-content>' +
          '<ul class="shop-by-category__list">' +
          subList +
          '</ul>' +
          '</div>' +
          '<button type="button" class="shop-by-category__toggle" hidden ' +
          'aria-expanded="false" aria-controls="shop-by-category-content-' +
          index +
          '" ' +
          'aria-label="' +
          escapeHtml(this.expandLabel) +
          '">' +
          PLUS_SVG +
          '</button>' +
          '</div>'
        );
      });

      this.grid.innerHTML = cards.join('');

      this.cards = Array.from(this.querySelectorAll('[data-shop-by-category-card]'));
      this.bindToggles();

      // Build searchable index from the DATA tree (captures each entry's full
      // <li> ancestor chain so a deep match can reveal its whole parent path).
      this.itemIndex = [];
      this.buildIndex(data);

      // Synchronous measure so the first paint already shows the fade gradient.
      this.measureOverflowSync();
    }

    buildIndex(data) {
      const entries = Object.entries(data);
      entries.forEach(([name, categoryData], i) => {
        const card = this.cards[i];
        if (!card) return;

        const heading = card.querySelector('[data-shop-by-category-name]');
        const headingLink = heading ? heading.querySelector('a') || heading : null;

        this.itemIndex.push({
          type: 'card',
          original: name,
          normalized: normalize(name),
          link: headingLink,
          lineage: [],
          card,
        });

        if (categoryData && categoryData.subcategories) {
          const rootUl = card.querySelector('.shop-by-category__list');
          this.indexSubcategories(categoryData.subcategories, rootUl, card, []);
        }
      });
    }

    indexSubcategories(subcategories, ulEl, card, ancestorLis) {
      if (!ulEl) return;
      const liEls = Array.from(ulEl.children).filter((el) => el.tagName === 'LI');
      const subEntries = Object.entries(subcategories);
      subEntries.forEach(([name, data], i) => {
        const li = liEls[i];
        if (!li) return;
        const link = li.querySelector(':scope > a');
        const lineage = ancestorLis.concat(li);

        this.itemIndex.push({
          type: 'item',
          original: name,
          normalized: normalize(name),
          link,
          lineage,
          card,
        });

        if (data && data.subcategories) {
          const childUl = li.querySelector(':scope > ul');
          if (childUl) this.indexSubcategories(data.subcategories, childUl, card, lineage);
        }
      });
    }

    /** Recursively build nested <ul>/<li> markup for the category tree. */
    buildList(subcategories) {
      if (!subcategories || typeof subcategories !== 'object') return '';
      const items = Object.entries(subcategories).map(([name, data]) => {
        const handle = data && data.handle ? data.handle : '';
        const nested = data && data.subcategories ? this.buildList(data.subcategories) : '';
        const nestedMarkup = nested ? '<ul>' + nested + '</ul>' : '';
        // A node without a handle isn't navigable — render plain text, not a
        // dead `href="#"` link. (The metafield SSR path always has a URL.)
        const label = handle ? '<a href="/collections/' + escapeHtml(handle) + '">' + escapeHtml(name) + '</a>' : '<span>' + escapeHtml(name) + '</span>';
        return '<li>' + label + nestedMarkup + '</li>';
      });
      return items.join('');
    }

    /* ---------- Shared behaviour (both sources) -------------------------- */

    bindToggles() {
      const toggleButtons = this.querySelectorAll('.shop-by-category__toggle');
      toggleButtons.forEach((btn) => {
        btn.addEventListener('click', this.onToggleClick);
        this.fetchCleanups.push(() => btn.removeEventListener('click', this.onToggleClick));
      });
    }

    /**
     * Measure each card's content and toggle the overflow state. Run
     * synchronously by default so the initial paint already includes the
     * `has-overflow` class — avoids a follow-up "fade-pop" flicker. Resize
     * handlers schedule via rAF (`updateOverflowState`) to coalesce bursts.
     */
    measureOverflowSync() {
      if (!this.cards) return;
      for (const card of this.cards) {
        if (card.hidden) continue;
        const content = card.querySelector('[data-shop-by-category-content]');
        const button = card.querySelector('.shop-by-category__toggle');
        if (!content || !button) continue;

        const isExpanded = content.classList.contains('is-expanded');
        if (isExpanded) {
          button.hidden = false;
          continue;
        }

        const overflows = content.scrollHeight > content.clientHeight;
        button.hidden = !overflows;
        content.classList.toggle('has-overflow', overflows);
      }
    }

    /** Resize-friendly wrapper that defers measurement to the next frame. */
    updateOverflowState() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.measureOverflowSync());
    }

    onToggleClick(e) {
      const button = e.currentTarget;
      const card = button.closest('[data-shop-by-category-card]');
      if (!card) return;
      const content = card.querySelector('[data-shop-by-category-content]');
      if (!content) return;

      const isExpanded = content.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      button.setAttribute('aria-label', isExpanded ? this.collapseLabel : this.expandLabel);
    }

    bindFilter() {
      if (!this.input) return;
      this.input.removeAttribute('disabled');
      this.input.addEventListener('input', this.onInput);
      this.fetchCleanups.push(() => this.input.removeEventListener('input', this.onInput));
    }

    bindResize() {
      window.addEventListener('resize', this.onResize, { passive: true });
      this.fetchCleanups.push(() => window.removeEventListener('resize', this.onResize));
    }

    onInput() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.applyFilter());
    }

    onResize() {
      // Re-evaluate overflow only when not actively filtering, since filter
      // mode forces all cards open.
      if (!this.input || this.input.value.trim().length === 0) {
        this.updateOverflowState();
      }
    }

    /**
     * Highlight every token occurrence inside a link's text. Operates on the
     * normalized form for matching, then maps the match range back to the
     * original string so diacritics still get `<mark>` at the right positions.
     */
    highlightLink(entry, tokens) {
      if (!entry.link || !buildNormMap) return;
      const original = entry.original;
      const { normalized, map } = buildNormMap(original);

      const ranges = [];
      for (const tok of tokens) {
        if (!tok) continue;
        let from = 0;
        while (from <= normalized.length - tok.length) {
          const idx = normalized.indexOf(tok, from);
          if (idx === -1) break;
          ranges.push([map[idx], map[idx + tok.length]]);
          from = idx + tok.length;
        }
      }
      if (ranges.length === 0) return;

      // Merge overlapping / adjacent ranges so a multi-token match doesn't
      // emit nested or back-to-back `<mark>` wrappers.
      ranges.sort((a, b) => a[0] - b[0]);
      const merged = [ranges[0].slice()];
      for (let i = 1; i < ranges.length; i++) {
        const last = merged[merged.length - 1];
        if (ranges[i][0] <= last[1]) last[1] = Math.max(last[1], ranges[i][1]);
        else merged.push(ranges[i].slice());
      }

      let html = '';
      let cursor = 0;
      for (const [s, e] of merged) {
        html += escapeHtml(original.slice(cursor, s));
        html += '<mark>' + escapeHtml(original.slice(s, e)) + '</mark>';
        cursor = e;
      }
      html += escapeHtml(original.slice(cursor));
      entry.link.innerHTML = html;
    }

    resetEntryLinks() {
      for (const entry of this.itemIndex) {
        if (entry.link && entry.link.querySelector('mark')) {
          entry.link.textContent = entry.original;
        }
      }
    }

    applyFilter() {
      const rawQuery = this.input.value.trim();
      // Whitespace-tokenized AND search: every token must appear somewhere in
      // the entry's normalized name (order-independent).
      const tokens =
        rawQuery.length === 0
          ? []
          : normalize(rawQuery)
              .split(/\s+/)
              .filter((t) => t.length > 0);
      const hasQuery = tokens.length > 0;

      this.resetEntryLinks();

      if (!hasQuery) {
        for (const entry of this.itemIndex) {
          if (entry.type === 'item' && entry.lineage.length > 0) {
            entry.lineage[entry.lineage.length - 1].hidden = false;
          }
        }
        for (const card of this.cards) {
          card.hidden = false;
          const content = card.querySelector('[data-shop-by-category-content]');
          if (content) content.classList.remove('is-expanded');
          const btn = card.querySelector('.shop-by-category__toggle');
          if (btn) {
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', this.expandLabel);
          }
        }
        if (this.empty) this.empty.hidden = true;
        this.classList.remove('shop-by-category--filter-empty');
        this.announceResultCount(null);
        this.updateOverflowState();
        return;
      }

      // Pre-pass: hide every <li>; we unhide along match paths only.
      for (const entry of this.itemIndex) {
        if (entry.type === 'item' && entry.lineage.length > 0) {
          entry.lineage[entry.lineage.length - 1].hidden = true;
        }
      }

      const matchingCards = new Set();
      const cardHeadingMatched = new Set();

      for (const entry of this.itemIndex) {
        const isMatch = tokens.every((tok) => entry.normalized.includes(tok));
        if (!isMatch) continue;

        matchingCards.add(entry.card);

        if (entry.type === 'card') {
          cardHeadingMatched.add(entry.card);
        } else {
          // Reveal the matching <li> AND every ancestor <li> on the way up.
          for (const li of entry.lineage) li.hidden = false;
        }
        this.highlightLink(entry, tokens);
      }

      // Heading match → show every descendant <li> (whole category requested).
      for (const card of cardHeadingMatched) {
        const lis = card.querySelectorAll('.shop-by-category__list li');
        for (const li of lis) li.hidden = false;
      }

      let visibleCount = 0;
      for (const card of this.cards) {
        const visible = matchingCards.has(card);
        card.hidden = !visible;
        if (visible) {
          visibleCount += 1;
          const content = card.querySelector('[data-shop-by-category-content]');
          if (content) {
            content.classList.add('is-expanded');
            content.classList.remove('has-overflow');
          }
          const btn = card.querySelector('.shop-by-category__toggle');
          if (btn) btn.hidden = true;
        }
      }

      const isFilterEmpty = visibleCount === 0;
      if (this.empty) this.empty.hidden = !isFilterEmpty;
      this.classList.toggle('shop-by-category--filter-empty', isFilterEmpty);
      this.announceResultCount(visibleCount);
    }

    announceResultCount(count) {
      if (!this.status) return;
      if (count === null) {
        this.status.textContent = '';
        return;
      }
      const tpl = this.dataset.shopByCategoryStatusTpl || '__COUNT__ results';
      this.status.textContent = tpl.replace('__COUNT__', String(count));
    }

    /**
     * Inject Schema.org ItemList of top-level categories (JSON source only;
     * the metafield source is server-rendered, so its category links are
     * already crawlable). Static crawlers miss this, but Googlebot executes JS.
     */
    injectJsonLd(data) {
      if (!data || typeof data !== 'object') return;
      const entries = Object.entries(data);
      if (entries.length === 0) return;

      const items = entries
        .filter(([, v]) => v && v.handle)
        .map(([name, v], i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Thing',
            name,
            url: window.location.origin + '/collections/' + v.handle,
          },
        }));

      if (items.length === 0) return;

      const heading = this.querySelector('.shop-by-category__heading');
      const ld = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: heading ? heading.textContent.trim() : 'Shop by category',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: items.length,
        itemListElement: items,
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(ld);
      this.appendChild(script);
      this.fetchCleanups.push(() => {
        if (script.parentNode) script.parentNode.removeChild(script);
      });
    }
  }

  customElements.define('shop-by-category', ShopByCategory);
})();
